import { useEffect, useMemo, useState } from "react";
import { DataTable } from "../components/DataTable";
import type { DataTableColumn } from "../components/DataTable";
import { getInventoryItems, restockInventoryItem } from "../services/inventoryService";



// ========================================
// TYPES
// ========================================

type InventoryStatus =
  | "in_stock"
  | "low"
  | "out";

type InventoryItem = {
  id: string;
  name: string;
  sku: string;
  category: string;
  stock: number;
  threshold: number;
  lastRestocked?: string | null;
};

// ========================================
// STATUS META
// ========================================

const statusMeta: Record<
  InventoryStatus,
  {
    bg: string;
    color: string;
    label: string;
  }
> = {
  in_stock: {
    bg: "#dcfce7",
    color: "#166534",
    label: "In Stock",
  },

  low: {
    bg: "#fef3c7",
    color: "#92400e",
    label: "Low Stock",
  },

  out: {
    bg: "#fee2e2",
    color: "#991b1b",
    label: "Out of Stock",
  },
};

// ========================================
// COMPONENT
// ========================================

export function InventoryPage() {
  const [inventory, setInventory] =
    useState<InventoryItem[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [restockId, setRestockId] =
    useState<string | null>(null);

  const [restockQty, setRestockQty] =
    useState(10);

  const [filterStatus, setFilterStatus] =
    useState<"all" | InventoryStatus>(
      "all"
    );

  // ========================================
  // LOAD INVENTORY
  // ========================================

  useEffect(() => {
    loadInventory();
  }, []);

  async function loadInventory() {
    try {
      setLoading(true);

      const response =
        await getInventoryItems();

      const mappedData: InventoryItem[] =
        response.map((item: any) => ({
          id: item.productID.toString(),

          name: item.productName,

          sku: item.sku,

          category: item.categoryName,

          stock: item.stockQty,

          threshold: item.minStockQty,

          lastRestocked:
            item.lastRestocked,
        }));

      setInventory(mappedData);
    } catch (error) {
      console.error(
        "Inventory load failed",
        error
      );
    } finally {
      setLoading(false);
    }
  }

  // ========================================
  // STATUS CALCULATOR
  // ========================================

  function getStatus(
    item: InventoryItem
  ): InventoryStatus {
    if (item.stock <= 0) {
      return "out";
    }

    if (item.stock <= item.threshold) {
      return "low";
    }

    return "in_stock";
  }

  // ========================================
  // RESTOCK
  // ========================================

  async function doRestock(id: string) {
    try {
      await restockInventoryItem(
        id,
        restockQty
      );

      setInventory((prev) =>
        prev.map((item) => {
          if (item.id !== id) {
            return item;
          }

          return {
            ...item,

            stock:
              item.stock + restockQty,

            lastRestocked:
              new Date().toISOString(),
          };
        })
      );

      setRestockId(null);

      setRestockQty(10);
    } catch (error) {
      console.error(
        "Restock failed",
        error
      );
    }
  }

  // ========================================
  // FILTERED DATA
  // ========================================

  const filteredRows = useMemo(() => {
    let rows = inventory;

    if (filterStatus !== "all") {
      rows = rows.filter(
        (x) =>
          getStatus(x) ===
          filterStatus
      );
    }

    return rows as unknown as Record<
      string,
      unknown
    >[];
  }, [inventory, filterStatus]);

  // ========================================
  // TABLE COLUMNS
  // ========================================

  const columns: DataTableColumn<
    Record<string, unknown>
  >[] = [
    {
      key: "name",

      label: "Product",

      sortable: true,

      render: (row) => (
        <div>
          <div className="inv-name">
            {String(row.name)}
          </div>

          <code className="inv-sku">
            {String(row.sku)}
          </code>
        </div>
      ),
    },

    {
      key: "category",

      label: "Category",

      sortable: true,
    },

    {
      key: "stock",

      label: "Stock",

      sortable: true,

      render: (row) => {
        const item =
          row as unknown as InventoryItem;

        const status =
          getStatus(item);

        const pct =
          item.stock === 0
            ? 0
            : Math.min(
                100,
                Math.round(
                  (item.stock /
                    (item.threshold *
                      3)) *
                    100
                )
              );

        const barColor =
          status === "in_stock"
            ? "#10b981"
            : status === "low"
            ? "#f59e0b"
            : "#ef4444";

        return (
          <div className="inv-stock-wrap">
            <div className="inv-stock-val">
              {item.stock} units
            </div>

            <div className="inv-bar-track">
              <div
                className="inv-bar-fill"
                style={{
                  width: `${pct}%`,
                  background:
                    barColor,
                }}
              />
            </div>
          </div>
        );
      },
    },

    {
      key: "threshold",

      label: "Threshold",

      sortable: true,

      render: (row) => (
        <span>
          {String(row.threshold)} units
        </span>
      ),
    },

    {
      key: "status",

      label: "Status",

      sortable: true,

      render: (row) => {
        const item =
          row as unknown as InventoryItem;

        const status =
          getStatus(item);

        return (
          <span
            className="pg-badge"
            style={{
              background:
                statusMeta[status].bg,

              color:
                statusMeta[status]
                  .color,
            }}
          >
            {
              statusMeta[status]
                .label
            }
          </span>
        );
      },
    },

    {
      key: "lastRestocked",

      label: "Last Restocked",

      sortable: true,

      render: (row) => {
        const date =
          row.lastRestocked
            ? new Date(
                String(
                  row.lastRestocked
                )
              )
            : null;

        return (
          <span>
            {date
              ? date.toLocaleDateString()
              : "-"}
          </span>
        );
      },
    },

    {
      key: "actions",

      label: "Actions",

      className: "text-end",

      render: (row) => {
        const id = String(row.id);

        return restockId === id ? (
          <div className="inv-edit-actions">
            <input
              type="number"
              min={1}
              value={restockQty}
              onChange={(e) =>
                setRestockQty(
                  Number(
                    e.target.value
                  )
                )
              }
              className="inv-qty-input"
              onClick={(e) =>
                e.stopPropagation()
              }
            />

            <button
              type="button"
              onClick={() =>
                doRestock(id)
              }
              className="inv-save-btn"
            >
              ✓ Add
            </button>

            <button
              type="button"
              onClick={() =>
                setRestockId(null)
              }
              className="inv-cancel-btn"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="d-flex justify-content-end">
            <button
              type="button"
              onClick={() =>
                setRestockId(id)
              }
              className="inv-edit-btn"
            >
              + Restock
            </button>
          </div>
        );
      },
    },
  ];

  // ========================================
  // STATS
  // ========================================

  const stats = [
    {
      label: "Total Stock",

      val: inventory.reduce(
        (a, i) => a + i.stock,
        0
      ),

      bg: "#dcfce7",

      color: "#166534",
    },

    {
      label: "Low Stock",

      val: inventory.filter(
        (i) =>
          getStatus(i) === "low"
      ).length,

      bg: "#fef3c7",

      color: "#92400e",
    },

    {
      label: "Out of Stock",

      val: inventory.filter(
        (i) =>
          getStatus(i) === "out"
      ).length,

      bg: "#fee2e2",

      color: "#991b1b",
    },

    {
      label: "Total SKUs",

      val: inventory.length,

      bg: "#e0e7ff",

      color: "#3730a3",
    },
  ];

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="d-grid gap-4">

      {/* ================================= */}
      {/* STATS */}
      {/* ================================= */}

      <div className="pg-stats-grid">
        {stats.map((s) => (
          <div
            key={s.label}
            className="pg-stat-card"
            style={{
              background: s.bg,
            }}
          >
            <div
              className="pg-stat-label"
              style={{
                color: s.color,
              }}
            >
              {s.label}
            </div>

            <div
              className="pg-stat-value"
              style={{
                color: s.color,
              }}
            >
              {s.val}
            </div>
          </div>
        ))}
      </div>

      {/* ================================= */}
      {/* FILTERS */}
      {/* ================================= */}

      <div className="pg-filter-bar">

        <button
          className={`pg-filter-btn ${
            filterStatus === "all"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilterStatus("all")
          }
        >
          All Items
        </button>

        <button
          className={`pg-filter-btn ${
            filterStatus === "low"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilterStatus("low")
          }
        >
          ⚠️ Low Stock
        </button>

        <button
          className={`pg-filter-btn ${
            filterStatus === "out"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setFilterStatus("out")
          }
        >
          ❌ Out of Stock
        </button>

      </div>

      {/* ================================= */}
      {/* TABLE */}
      {/* ================================= */}

      <DataTable
        title="Inventory"
        rows={filteredRows}
        columns={columns}
        rowKey={(row) =>
          String(row.id)
        }
        searchPlaceholder="Search inventory..."
        exportFileName="inventory"
        pageSize={10}
       // loading={loading}
      />

    </div>
  );
}